/**
 * Utility functions for exporting graph data and visuals in various formats.
 */

/**
 * Downloads the current graph view as a PNG image.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadImagePNG = (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene to the WebGL renderer
    renderer.render(scene, camera);

    // Use the WebGL context to capture the current frame
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    // Create a new ArrayBuffer and read pixel data into it
    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Create a 2D canvas to assemble the image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    // Flip the image data and put it onto the canvas
    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    context.putImageData(imageData, 0, 0);
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(canvas, 0, 0);

    // Convert the canvas to a PNG data URL
    const imageURL = canvas.toDataURL('image/png');

    // Create a link to trigger the download
    const link = document.createElement('a');
    link.download = 'futco.png';
    link.href = imageURL;
    link.click();
  }
};

/**
 * Downloads the current graph view as a JPG image.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadImageJPG = (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene
    renderer.render(scene, camera);

    // Capture the frame as a JPEG URL
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    context.putImageData(imageData, 0, 0);
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(canvas, 0, 0);

    const imageURL = canvas.toDataURL('image/jpeg', 0.9);

    const link = document.createElement('a');
    link.download = 'futco.jpg';
    link.href = imageURL;
    link.click();
  }
};

/**
 * Downloads the current graph view as a PDF document.
 * @param {Object} graphRef - Reference to the graph component for rendering.
 */
export const downloadPDF = async (graphRef) => {
  if (graphRef.current) {
    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    // Render the scene
    renderer.render(scene, camera);

    // Capture the frame as a JPEG URL
    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    context.putImageData(imageData, 0, 0);
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(canvas, 0, 0);

    const imageURL = canvas.toDataURL('image/jpeg', 0.9);

    // Dynamically import jsPDF only when needed
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'mm'
    });

    const pdfWidth = doc.internal.pageSize.getWidth();
    const pdfHeight = doc.internal.pageSize.getHeight();
    const margin = 10; // 10mm margins
    const imageWidth = pdfWidth - (margin * 2);
    const imageHeight = (height * imageWidth) / width;
    let finalImageHeight = imageHeight;
    let finalImageWidth = imageWidth;
    if (imageHeight > pdfHeight - (margin * 2)) {
      finalImageHeight = pdfHeight - (margin * 2);
      finalImageWidth = (width * finalImageHeight) / height;
    }
    const xOffset = (pdfWidth - finalImageWidth) / 2;
    const yOffset = (pdfHeight - finalImageHeight) / 2;

    doc.setFontSize(16);
    doc.text('Futco Graph', pdfWidth / 2, margin, { align: 'center' });

    doc.addImage(imageURL, 'JPEG', xOffset, yOffset, finalImageWidth, finalImageHeight);

    const currentDate = new Date().toLocaleDateString();
    doc.setFontSize(12);
    doc.text(`Date: ${currentDate}`, margin, pdfHeight - margin);

    doc.save('futco-graph.pdf');
  }
};

/**
 * Converts graph data to CSV format.
 * @param {Object} graphData - The graph data containing nodes.
 * @param {Array} tokenList - List of tokens for additional data.
 * @returns {string} - CSV formatted string.
 */
export const graphDataToCsv = (graphData, tokenList) => {
  let csvContent = "Name,Symbol,Address,Price\n"; // CSV Header

  graphData.nodes.forEach(node => {
    const token = tokenList.find(token => token.id === node.id);
    if (token) {
      csvContent += `${node.label},${token.symbol},${node.id},${token.price}\n`;
    }
  });

  return csvContent;
};

/**
 * Downloads data as a CSV file.
 * @param {string} csvContent - The CSV content to download.
 * @param {string} fileName - The name of the file to download.
 */
export const downloadCsv = (csvContent, fileName) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
